"use client";

import { m, type Variants } from "motion/react";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { revealUp } from "./variants";
import { duration, easing } from "./tokens";

const defaultContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05,
      ease: easing.out,
      duration: duration.md,
    },
  },
};

interface StaggerGroupProps {
  children: ReactNode;
  /** Render-as element. Defaults to `div`. */
  as?: ElementType;
  /** Override the container variants if you want a different stagger feel. */
  variants?: Variants;
  amount?: number | "some" | "all";
  /** Trigger once (default) or every time it enters the viewport. */
  once?: boolean;
  className?: string;
}

/**
 * Container half of the stagger pair. Pair with `<StaggerItem>` children.
 *
 * The group itself has no visual change — it owns the `whileInView`
 * trigger and tells Motion to fan its children out across time.
 */
export function StaggerGroup({
  children,
  as,
  variants = defaultContainer,
  amount = "some",
  once = true,
  className,
  ...rest
}: StaggerGroupProps &
  Omit<ComponentPropsWithoutRef<ElementType>, keyof StaggerGroupProps>) {
  const Component = (m[(as ?? "div") as keyof typeof m] ??
    m.div) as typeof m.div;

  return (
    <Component
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={variants}
      {...rest}
    >
      {children}
    </Component>
  );
}

interface StaggerItemProps {
  children: ReactNode;
  /** Render-as element. Defaults to `div`. */
  as?: ElementType;
  /** Override the per-item variants if you want a different motion. */
  variants?: Variants;
  className?: string;
}

/**
 * Child half of the stagger pair. Each item inherits the parent's
 * trigger but uses its own variants for the actual visual change —
 * `revealUp` by default.
 */
export function StaggerItem({
  children,
  as,
  variants = revealUp,
  className,
  ...rest
}: StaggerItemProps &
  Omit<ComponentPropsWithoutRef<ElementType>, keyof StaggerItemProps>) {
  const Component = (m[(as ?? "div") as keyof typeof m] ??
    m.div) as typeof m.div;

  return (
    <Component className={className} variants={variants} {...rest}>
      {children}
    </Component>
  );
}
